/**
 * THE FRONT DOOR.
 *
 * The first screen anyone ever sees, so it has three jobs in this order: say
 * what this is, get them into a room in one tap, and show the records — so the
 * room they walk into has stakes before they have fired a shot.
 */

import { useEffect, useState } from 'react';
import type { Snapshot } from '../net/connection.js';
import { createSaloon, joinSaloon, setHandle } from '../net/connection.js';
import { CODE_LENGTH, cleanHandle, normalizeCode } from '@shared/protocol.js';
import { Pip } from '../ui/Pip.js';
import { Antlers } from '../ui/Antlers.js';
import { hasFinePointer } from '../ui/settings.js';

interface Props {
  snap: Snapshot;
  onRefresh: () => void;
  onOpenSettings: () => void;
  onRange: () => void;
  onTouchAudio: () => void;
}

export function Doors({ snap, onRefresh, onOpenSettings, onRange, onTouchAudio }: Props) {
  const [name, setName] = useState(snap.handle);
  const [code, setCode] = useState('');
  const [tab, setTab] = useState<'rooms' | 'records'>('rooms');

  useEffect(() => {
    // A deep link like /?s=ABCDE walks straight into a room. The code is not a
    // secret from whoever holds the link — it IS the invitation — and
    // `Referrer-Policy: no-referrer` keeps it from leaking anywhere else.
    const deep = normalizeCode(new URLSearchParams(location.search).get('s') ?? '');
    if (deep.length === CODE_LENGTH) setCode(deep);
  }, []);

  const handle = cleanHandle(name);
  const canGo = snap.status === 'open';
  // The tagline is the first instruction anyone gets, so it has to be true of
  // the machine they are reading it on.
  const aimWord = hasFinePointer() ? 'Point and click' : 'Point the phone';

  const go = (fn: () => void) => {
    onTouchAudio();
    setHandle(handle || 'Stranger');
    fn();
  };

  return (
    <main className="room room-wide">
      <div className="doors">
        <Antlers className="antlers" />
        <h1 className="marquee">2 Buck Saloon</h1>
        <p className="sub">{aimWord} · Take the buck · Leave the doe</p>
      </div>

      {/* One wrapper, two halves. It is a plain block on a phone and a
          two-column grid past 62rem — the DOM order never changes, so tab
          order and screen-reader order are identical at every width. */}
      <div className="room-cols">
        <div>
      <div className="panel">
        <label className="tiny muted" htmlFor="handle">
          What do they call you
        </label>
        <input
          id="handle"
          className="field mt-xs"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 14))}
          onBlur={() => setHandle(handle || 'Stranger')}
          placeholder="Stranger"
          maxLength={14}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
        />

        <button
          className="btn btn-primary btn-big btn-wide mt-md"
          disabled={!canGo}
          onClick={() => go(() => createSaloon(true))}
        >
          Push through the doors
        </button>
        <p className="tiny muted mt-xs mb-0">Opens a saloon anyone can walk into. Eight at the rail.</p>

        <hr className="rule" />

        <div className="row">
          <input
            className="field grow mono"
            value={code}
            onChange={(e) => setCode(normalizeCode(e.target.value))}
            placeholder="CODE"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={CODE_LENGTH}
            aria-label="Saloon code"
            enterKeyHint="go"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === CODE_LENGTH) go(() => joinSaloon(code));
            }}
          />
          <button className="btn" disabled={!canGo || code.length !== CODE_LENGTH} onClick={() => go(() => joinSaloon(code))}>
            Join
          </button>
        </div>

        <div className="row mt-sm">
          <button className="btn grow" disabled={!canGo} onClick={() => go(() => createSaloon(false))}>
            Private room
          </button>
          <button className="btn grow" onClick={onRange}>
            Practice range
          </button>
        </div>
      </div>
        </div>

        <div>
      <div className="panel">
        <div className="row">
          <div className="pill-group" role="group" aria-label="What to show">
            <button aria-pressed={tab === 'rooms'} onClick={() => setTab('rooms')}>
              Open rooms
            </button>
            <button
              aria-pressed={tab === 'records'}
              onClick={() => {
                setTab('records');
                onRefresh();
              }}
            >
              House records
            </button>
          </div>
          <button className="btn btn-ghost" onClick={onRefresh} aria-label="Refresh the list">
            ↻
          </button>
        </div>

        {tab === 'rooms' ? <Rooms snap={snap} canGo={canGo} go={go} /> : <Records snap={snap} />}
      </div>

      <div className="panel">
        <details>
          <summary className="toggle">House rules</summary>
          <ul className="bullets">
            <li>
              Everyone in the room shoots the <strong>same herd</strong>. First shot on a buck takes it.
            </li>
            <li>
              <strong>Bucks only.</strong> Shoot a doe and you are out for the rest of that wave — and
              everyone gets told, by name.
            </li>
            <li>Six shells. Fire the last one and it racks itself.</li>
            <li>Bigger rack, longer shot, faster deer, better placement — all worth more.</li>
            <li>Ten waves, five sites. There is something very large in the last one.</li>
            <li>
              Aim by pointing the phone, with a thumb, or with a mouse. Same accuracy ceiling, same
              leaderboard — pick whichever you have.
            </li>
          </ul>
        </details>
      </div>
        </div>
      </div>

      <div className="row between mt-md">
        <Pip status={snap.status} rtt={snap.rtt} />
        <button className="btn btn-ghost" onClick={onOpenSettings}>
          Settings
        </button>
      </div>
    </main>
  );
}

function Rooms({ snap, canGo, go }: { snap: Snapshot; canGo: boolean; go: (fn: () => void) => void }) {
  if (snap.saloons.length === 0) {
    return <p className="muted fs-md mt-md mb-0">Quiet in here. Push through the doors and be the first one at the rail.</p>;
  }
  return (
    <ul className="list">
      {snap.saloons.map((s) => (
        <li key={s.code} className="list-row">
          <span className="mono heavy">{s.code}</span>
          <span className="muted fs-sm ellipsis">{s.hostHandle}&rsquo;s round</span>
          <span className="score tiny muted">
            {s.seats}/{s.maxSeats}
            {s.phase !== 'bar' ? ` · wave ${s.waveIndex}` : ''}
          </span>
          <button className="btn" onClick={() => go(() => joinSaloon(s.code))} disabled={!canGo}>
            {s.phase === 'bar' ? 'Join' : 'Watch'}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Records({ snap }: { snap: Snapshot }) {
  const board = snap.board;
  if (!board) return <p className="muted mt-md mb-0">Asking the bartender…</p>;
  if (board.scores.length === 0 && board.racks.length === 0) {
    return <p className="muted fs-md mt-md mb-0">Nothing on the wall yet. The first full hunt goes up here.</p>;
  }
  return (
    <>
      {board.scores.length > 0 && (
        <>
          <p className="tiny muted mt-md mb-0">Best hunts</p>
          <ul className="list">
            {board.scores.slice(0, 8).map((e, i) => (
              <li key={`s${i}`} className="list-row">
                <span className="num">{i + 1}</span>
                <span className="bold ellipsis">{e.handle}</span>
                <span className="score">{e.score.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {board.racks.length > 0 && (
        <>
          <p className="tiny muted mt-lg mb-0">Biggest racks</p>
          <ul className="list">
            {board.racks.slice(0, 5).map((e, i) => (
              <li key={`r${i}`} className="list-row">
                <span className="num">{i + 1}</span>
                <span className="bold ellipsis">{e.handle}</span>
                <span className="score">{e.bestRack} pt</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
